import { mockRequest } from '../mock/api';

const BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';
const TOKEN_KEY = 'wxp.token';

/**
 * The client answers its own requests from src/lib/mock/api.ts, so the UI runs with
 * nothing else started. Set VITE_MOCK_API=false once the Go backend is up.
 */
const USE_MOCK = (import.meta.env.VITE_MOCK_API ?? 'true') !== 'false';

/**
 * Opens a long-lived response (server-sent events) with the session's token
 * in a header, which EventSource cannot send. The caller reads the body.
 * Mock mode has no streams: it answers 404, as a well without a feed does.
 */
export async function openStream(path: string, signal: AbortSignal): Promise<Response> {
  if (USE_MOCK) throw new ApiError('not found', 404);
  const headers = new Headers({ Accept: 'text/event-stream' });
  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { headers, signal });
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return res;
}

/** Exposed so the sign-in page can say which accounts actually work. */
export const usingMockApi = USE_MOCK;

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — session-only auth */
  }
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

interface RequestOptions {
  /**
   * Leave a 401 to the caller instead of sending the person to sign in. For
   * calls the person did not make, like reporting an error in the background.
   */
  keepPage?: boolean;
}

export async function request<T>(path: string, init: RequestInit = {}, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');

  const token = getToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  if (USE_MOCK) {
    const { status, body } = await mockRequest(path, init, token);
    if (status >= 400) {
      const err = body as { error?: string };
      throw new ApiError(err?.error ?? `Request failed (${status})`, status);
    }
    return body as T;
  }

  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, { ...init, headers });
  } catch {
    throw new ApiError('Cannot reach the WellXP server. Is the backend running?', 0);
  }

  if (res.status === 204) return undefined as T;

  const body = await res.json().catch(() => ({}));

  // A 401 is a session the server no longer knows — most often because the
  // API restarted and sessions live in its memory. Leaving the person on a
  // page where every action answers "forbidden" explains nothing; sending them
  // to sign in again does. The sign-in call itself is exempt, or a wrong
  // password would reload the page. Where they were goes along as ReturnUrl,
  // so signing back in lands on the same rail and folder, not on Overview.
  if (res.status === 401 && path !== '/auth/login' && !options.keepPage) {
    setToken(null);
    if (window.location.pathname !== '/login') {
      const here = window.location.pathname + window.location.search;
      window.location.assign(`/login?ReturnUrl=${encodeURIComponent(here)}`);
    }
  }

  if (!res.ok) {
    throw new ApiError(body?.error ?? `Request failed (${res.status})`, res.status);
  }
  return body as T;
}
